import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import SearchBar from "../../components/common/SearchBar";
import UserProfileContext from "../../context/userProfile/UserProfileContext";
import OrdersAPI from "../../api/firebase/OrdersAPI";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import DeliveryDiningOutlinedIcon from "@mui/icons-material/DeliveryDiningOutlined";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import PaymentIcon from "@mui/icons-material/Payment";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useNavigate } from "react-router-dom";
import ProductsContainer from "../../components/app/Products/ProductsContainer";
import AllProductContext from "../../context/allProducts/AllProductsContext";
import RecentlyViewedContext from "../../context/recentlyViewed/RecentlyViewedContext";
import { formatDateToCustomFormat, getDateFromCreatedAt } from "../../utils/dateUtils";

const BRAND_COLOR = process.env.REACT_APP_BG_COLOR || "#075e54";

const getOrderTime = (order) => {
  const rawDate = order?.createdAt || order?.modifiedAt || order?.orderDate;
  if (!rawDate) return 0;
  return getDateFromCreatedAt(rawDate).getTime() || 0;
};

const sortOrdersByLatest = (orderList) => {
  if (!Array.isArray(orderList)) return [];
  return [...orderList].sort((a, b) => getOrderTime(b) - getOrderTime(a));
};

const UserOrdersPage = () => {
  const userProfileContext = useContext(UserProfileContext);
  const allProductsContext = useContext(AllProductContext);
  const recentlyViewedContext = useContext(RecentlyViewedContext);
  const recentlyViewedItemsArray = recentlyViewedContext.state;
  const [orders, setOrders] = useState([]);
  const [detailedOrders, setDetailedOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const ordersAPI = useMemo(() => new OrdersAPI(), []);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [searchText, setSearchText] = useState("");
  const [copiedOrderId, setCopiedOrderId] = useState("");
  const navigate = useNavigate();

  const handleCopyOrderId = (e, orderId) => {
    e.stopPropagation();
    if (!orderId) return;
    navigator.clipboard?.writeText?.(String(orderId));
    setCopiedOrderId(orderId);
    setTimeout(() => {
      setCopiedOrderId("");
    }, 2000);
  };



  useEffect(() => {
    console.log(
      "🔍 UserOrdersPage: userProfileContext.state changed:",
      userProfileContext.state
    );

    if (userProfileContext.state !== null) {
      setUser(userProfileContext.state);
      console.log("👤 User set:", userProfileContext.state);
    }

    if (
      userProfileContext.state !== null &&
      userProfileContext.state.orders !== undefined
    ) {
      console.log("📋 Raw user orders:", userProfileContext.state.orders);
      console.log(
        "📊 Number of orders:",
        userProfileContext.state.orders.length
      );

      // Log each order status
      userProfileContext.state.orders.forEach((order, index) => {
        console.log(
          `📦 Order ${index + 1}: ID=${order.id}, Status=${
            order.status
          }, CreatedAt=${order.createdAt}`
        );
      });

      const rawOrders = Array.isArray(userProfileContext.state.orders)
        ? userProfileContext.state.orders
        : [];
      const sortedOrders = sortOrdersByLatest(rawOrders);
      console.log("📋 Sorted orders:", sortedOrders);
      setOrders(sortedOrders);
    } else {
      console.log("❌ No orders found in user context");
      setOrders([]);
    }
  }, [userProfileContext.state]);

  useEffect(() => {
    let isCancelled = false;

    if (orders && orders.length > 0) {
      setLoading(true);
      Promise.all(
        orders.map(async (order) => {
          try {
            const res = await ordersAPI.getOrderDetailById(order.id);
            if (res) {
              return { ...order, ...res, id: order.id || res.id };
            }
            return order;
          } catch (err) {
            console.error("Failed to load order detail for ID:", order.id, err);
            return order;
          }
        })
      )
        .then((orderDetail) => {
          if (isCancelled) return;
          const validOrders = orderDetail.filter(Boolean);
          const sorted = sortOrdersByLatest(validOrders);
          setDetailedOrders(sorted);
          setLoading(false);
        })
        .catch((err) => {
          if (isCancelled) return;
          console.error("Error loading order details:", err);
          setDetailedOrders(sortOrdersByLatest(orders));
          setLoading(false);
        });
    } else {
      setDetailedOrders([]);
      setLoading(false);
    }

    return () => {
      isCancelled = true;
    };
  }, [orders, ordersAPI]);

  useEffect(() => {
    if (searchText.length > 2) {
      const filtered = detailedOrders.filter((order) => {
        let allTitles = "";
        const items = Array.isArray(order.items) ? order.items : [];
        items.forEach((item) => {
          allTitles = allTitles + " " + (item?.title || "");
        });
        const orderIdMatch = String(order?.id || "")
          .toLowerCase()
          .includes(searchText.toLowerCase());
        return (
          allTitles.toLowerCase().includes(searchText.toLowerCase()) ||
          orderIdMatch
        );
      });
      setFilteredOrders(sortOrdersByLatest(filtered));
    } else {
      setFilteredOrders(sortOrdersByLatest(detailedOrders));
    }
  }, [detailedOrders, searchText]);
  // useEffect(() => {
  //   console.log("filteredOrders CHANGED:", filteredOrders);
  // }, [filteredOrders]);

  function onSearchTextChange(e) {
    const enteredValue = e.currentTarget.value;
    setSearchText(enteredValue);
  }
  const isPaymentProcessing = (order) => {
    // If user closed the payment modal, do not consider it processing
    if (order?.payment?.modalClosed === true) {
      return false;
    }
    const orderAt = getDateFromCreatedAt(order.createdAt);
    const nowDate = new Date();

    // date diffrence in minutes
    const diff = (nowDate - orderAt) / 60000;
    // console.log(
    //   "order at:" + orderAt + " nowDate:" + nowDate + "\n diff:" + diff
    // );
    if (diff < 5) {
      return true;
    }
    return false;
  };

  return (
    <>
      {!loading && (
        <Container
          maxWidth={detailedOrders.length > 0 ? "lg" : "lg"}
          sx={{
            marginTop: { xs: "1rem", sm: "2rem" },
            marginBottom: { xs: "1rem", sm: "2rem" },
            px: { xs: 1, sm: 2 },
          }}
        >
          {detailedOrders.length > 0 && (
            <>
              {/* Enhanced Header Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: "text.primary",
                    fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
                  }}
                >
                  Your Orders
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Track and manage all your orders in one place
                </Typography>

                <SearchBar
                  placeholderText="Search your orders..."
                  variant="border"
                  onSearchTextChange={onSearchTextChange}
                  searchText={searchText}
                />
              </Box>
            </>
          )}

          {/* Enhanced Order Cards */}
          {!loading &&
            filteredOrders.length > 0 &&
            filteredOrders.map((order, index) => {
              const orderPlacedDate = getDateFromCreatedAt(order.createdAt);
              const expectedDeliveryDate = new Date(orderPlacedDate.getTime() + 8 * 24 * 60 * 60 * 1000);
              const expectedDD = formatDateToCustomFormat(expectedDeliveryDate);

              // Enhanced status logic with rich styling and icons
              let statusInfo = {};
              switch (order.status.toUpperCase()) {
                case "CREATED":
                  if (isPaymentProcessing(order)) {
                    statusInfo = {
                      label: "Processing Payment",
                      subtitle: "Verifying with your bank",
                      badgeColor: "#b45309",
                      badgeBg: "#fffbeb",
                      badgeBorder: "#fde68a",
                      icon: <PaymentIcon sx={{ fontSize: 16 }} />,
                    };
                  } else {
                    statusInfo = {
                      label: "Payment Pending",
                      subtitle: "Complete payment to confirm order",
                      badgeColor: "#b91c1c",
                      badgeBg: "#fef2f2",
                      badgeBorder: "#fecaca",
                      isPaymentPending: true,
                      icon: <PaymentIcon sx={{ fontSize: 16 }} />,
                    };
                  }
                  break;
                case "PLACED":
                  statusInfo = {
                    label: "Order Confirmed",
                    subtitle: `Expected delivery: ${expectedDD}`,
                    badgeColor: "#047857",
                    badgeBg: "#ecfdf5",
                    badgeBorder: "#a7f3d0",
                    icon: <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />,
                  };
                  break;
                case "SHIPPED":
                  statusInfo = {
                    label: "Shipped",
                    subtitle: order.courierDetails?.partner
                      ? `Shipped via ${order.courierDetails.partner}`
                      : "On the way to you",
                    badgeColor: "#0284c7",
                    badgeBg: "#f0f9ff",
                    badgeBorder: "#bae6fd",
                    icon: <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />,
                  };
                  break;
                case "OUT FOR DELIVERY":
                  statusInfo = {
                    label: "Out for Delivery",
                    subtitle: "Will be delivered today",
                    badgeColor: "#d97706",
                    badgeBg: "#fffbeb",
                    badgeBorder: "#fde68a",
                    icon: <DeliveryDiningOutlinedIcon sx={{ fontSize: 16 }} />,
                  };
                  break;
                case "DELIVERED":
                  statusInfo = {
                    label: "Delivered",
                    subtitle: `Delivered on ${
                      order.deliveredAt ? formatDateToCustomFormat(getDateFromCreatedAt(order.deliveredAt)) : "N/A"
                    }`,
                    badgeColor: "#047857",
                    badgeBg: "#ecfdf5",
                    badgeBorder: "#a7f3d0",
                    icon: <TaskAltRoundedIcon sx={{ fontSize: 16 }} />,
                  };
                  break;
                case "CANCELLED BY BUYER":
                case "CANCELLED BY SELLER":
                case "CANCELLED":
                  const isSeller = order.status.toUpperCase().includes("SELLER");
                  statusInfo = {
                    label: isSeller ? "Cancelled by Seller" : "Cancelled",
                    subtitle: `Cancelled on ${
                      order.cancelledAt ? formatDateToCustomFormat(getDateFromCreatedAt(order.cancelledAt)) : "N/A"
                    }`,
                    badgeColor: "#b91c1c",
                    badgeBg: "#fef2f2",
                    badgeBorder: "#fecaca",
                    icon: <CancelRoundedIcon sx={{ fontSize: 16 }} />,
                  };
                  break;
                default:
                  statusInfo = {
                    label: order.status || "Placed",
                    subtitle: `Expected delivery: ${expectedDD}`,
                    badgeColor: "#047857",
                    badgeBg: "#ecfdf5",
                    badgeBorder: "#a7f3d0",
                    icon: <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />,
                  };
              }

              const items = Array.isArray(order.items) ? order.items : [];

              return (
                <Card
                  key={order.id || index}
                  elevation={0}
                  sx={{
                    mb: { xs: 2, sm: 2.5 },
                    cursor: "pointer",
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                    borderRadius: { xs: 2.5, sm: 3.5 },
                    border: "1px solid #e2e8f0",
                    bgcolor: "#ffffff",
                    overflow: "hidden",
                    "&:hover": {
                      transform: { xs: "none", sm: "translateY(-3px)" },
                      boxShadow: "0 12px 28px -6px rgba(0, 0, 0, 0.08)",
                      borderColor: "rgba(7, 94, 84, 0.35)",
                    },
                    "&:active": {
                      bgcolor: { xs: "#f8fafc", sm: "#ffffff" },
                    },
                  }}
                  onClick={() =>
                    navigate("/orderDetail", { state: { order: order } })
                  }
                >
                  {/* Top Header Strip: Mobile-first responsive layout */}
                  <Box
                    sx={{
                      px: { xs: 1.75, sm: 3 },
                      py: { xs: 1.25, sm: 1.5 },
                      bgcolor: "#f8fafc",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    {/* Primary Meta Row: Placed date and Total Amount */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Stack direction="row" spacing={{ xs: 1.5, sm: 4 }} alignItems="center">
                        {/* Order Placed Date */}
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#64748b",
                              fontWeight: 700,
                              letterSpacing: "0.05em",
                              textTransform: "uppercase",
                              fontSize: { xs: "0.625rem", sm: "0.6875rem" },
                              display: "block",
                            }}
                          >
                            Order Placed
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#1e293b",
                              fontSize: { xs: "0.8125rem", sm: "0.85rem" },
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatDateToCustomFormat(orderPlacedDate)}
                          </Typography>
                        </Box>

                        {/* Ship To Recipient (Desktop) */}
                        <Box sx={{ display: { xs: "none", sm: "block" } }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#64748b",
                              fontWeight: 700,
                              letterSpacing: "0.05em",
                              textTransform: "uppercase",
                              fontSize: "0.6875rem",
                              display: "block",
                            }}
                          >
                            Ship To
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: "#1e293b", fontSize: "0.85rem" }}
                          >
                            {order.address?.name || "Customer"}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Total Amount & Desktop Order ID */}
                      <Stack direction="row" spacing={3} alignItems="center">
                        <Box sx={{ textAlign: "right" }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#64748b",
                              fontWeight: 700,
                              letterSpacing: "0.05em",
                              textTransform: "uppercase",
                              fontSize: { xs: "0.625rem", sm: "0.6875rem" },
                              display: "block",
                            }}
                          >
                            Total
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: BRAND_COLOR,
                              fontSize: { xs: "0.925rem", sm: "1rem" },
                              whiteSpace: "nowrap",
                            }}
                          >
                            ₹{order.amount}
                          </Typography>
                        </Box>

                        {/* Order ID & 1-Click Copy (Desktop) */}
                        <Box
                          sx={{
                            display: { xs: "none", sm: "flex" },
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: copiedOrderId === order.id ? "#16a34a" : "#64748b",
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              fontFamily: "monospace",
                            }}
                          >
                            {copiedOrderId === order.id ? "ID Copied!" : `Order #${order.id}`}
                          </Typography>
                          <Tooltip title={copiedOrderId === order.id ? "Copied!" : "Copy Order ID"}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleCopyOrderId(e, order.id)}
                              aria-label="Copy Order ID"
                              sx={{
                                color: copiedOrderId === order.id ? "#16a34a" : "#94a3b8",
                                p: 0.5,
                                bgcolor: copiedOrderId === order.id ? "#dcfce7" : "transparent",
                                "&:hover": { color: BRAND_COLOR, bgcolor: "rgba(7, 94, 84, 0.08)" },
                              }}
                            >
                              {copiedOrderId === order.id ? (
                                <CheckCircleRoundedIcon sx={{ fontSize: 14 }} />
                              ) : (
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Stack>
                    </Box>

                    {/* Secondary Row for Mobile: Order ID + Ship To */}
                    <Box
                      sx={{
                        display: { xs: "flex", sm: "none" },
                        alignItems: "center",
                        justifyContent: "space-between",
                        mt: 1,
                        pt: 0.75,
                        borderTop: "1px dashed #e2e8f0",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: copiedOrderId === order.id ? "#16a34a" : "#64748b",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            fontFamily: "monospace",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {copiedOrderId === order.id ? "ID Copied!" : `Order #${order.id}`}
                        </Typography>
                        <Tooltip title={copiedOrderId === order.id ? "Copied!" : "Copy Order ID"}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleCopyOrderId(e, order.id)}
                            aria-label="Copy Order ID"
                            sx={{
                              color: copiedOrderId === order.id ? "#16a34a" : "#94a3b8",
                              p: 0.4,
                              bgcolor: copiedOrderId === order.id ? "#dcfce7" : "transparent",
                            }}
                          >
                            {copiedOrderId === order.id ? (
                              <CheckCircleRoundedIcon sx={{ fontSize: 13 }} />
                            ) : (
                              <ContentCopyIcon sx={{ fontSize: 13 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {order.address?.name && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748b",
                            fontWeight: 500,
                            fontSize: "0.725rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "45%",
                          }}
                        >
                          To: <strong>{order.address.name}</strong>
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Main Card Body */}
                  <CardContent sx={{ p: { xs: 1.75, sm: 2.5 } }}>
                    {/* Responsive Content Row: Side-by-side on mobile, 3-column on desktop */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: { xs: "flex-start", sm: "center" },
                        gap: { xs: 1.5, sm: 2.5 },
                      }}
                    >
                      {/* Product Thumbnail(s) */}
                      {/* MOBILE: Single primary thumbnail with overlay badge */}
                      <Box
                        sx={{
                          display: { xs: "block", sm: "none" },
                          position: "relative",
                          width: 72,
                          height: 72,
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            borderRadius: 2,
                            border: "1px solid #e2e8f0",
                            bgcolor: "#f8fafc",
                            p: 0.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            component="img"
                            src={
                              items[0]?.images?.[0]?.url ||
                              items[0]?.image ||
                              items[0]?.thumbnail ||
                              "/logo192.png"
                            }
                            alt={items[0]?.title || "Product"}
                            sx={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </Box>
                        {items.length > 1 && (
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: -4,
                              right: -4,
                              bgcolor: "#0f172a",
                              color: "#ffffff",
                              borderRadius: "10px",
                              px: 0.7,
                              py: 0.15,
                              fontSize: "0.625rem",
                              fontWeight: 700,
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                              border: "1.5px solid #ffffff",
                              lineHeight: 1.2,
                            }}
                          >
                            +{items.length - 1}
                          </Box>
                        )}
                      </Box>

                      {/* DESKTOP: Multi-thumbnail row */}
                      <Box
                        sx={{
                          display: { xs: "none", sm: "flex" },
                          alignItems: "center",
                          gap: 1,
                          flexShrink: 0,
                        }}
                      >
                        {items.slice(0, 3).map((item, idx) => {
                          const imgUrl =
                            item?.images?.[0]?.url || item?.image || item?.thumbnail || "/logo192.png";
                          return (
                            <Box
                              key={idx}
                              sx={{
                                width: 68,
                                height: 68,
                                borderRadius: 2,
                                border: "1px solid #e2e8f0",
                                bgcolor: "#f8fafc",
                                p: 0.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                component="img"
                                src={imgUrl}
                                alt={item?.title || "Product"}
                                sx={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            </Box>
                          );
                        })}

                        {items.length > 3 && (
                          <Box
                            sx={{
                              width: 42,
                              height: 68,
                              borderRadius: 2,
                              border: "1px dashed #cbd5e1",
                              bgcolor: "#f1f5f9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#475569",
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              flexShrink: 0,
                            }}
                          >
                            +{items.length - 3}
                          </Box>
                        )}
                      </Box>

                      {/* Middle: Status & Item Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack spacing={0.75}>
                          {/* Status Pill Badge & Icon */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.6,
                                px: { xs: 1, sm: 1.25 },
                                py: { xs: 0.35, sm: 0.4 },
                                borderRadius: "20px",
                                bgcolor: statusInfo.badgeBg,
                                color: statusInfo.badgeColor,
                                border: `1px solid ${statusInfo.badgeBorder}`,
                                fontWeight: 700,
                                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                                lineHeight: 1.2,
                              }}
                            >
                              {statusInfo.icon}
                              <span>{statusInfo.label}</span>
                            </Box>

                            {/* Subtitle / Delivery estimate */}
                            {statusInfo.subtitle && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#475569",
                                  fontWeight: 500,
                                  fontSize: { xs: "0.725rem", sm: "0.75rem" },
                                }}
                              >
                                • {statusInfo.subtitle}
                              </Typography>
                            )}
                          </Box>

                          {/* Item Titles */}
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#0f172a",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              lineHeight: 1.35,
                              fontSize: { xs: "0.84rem", sm: "0.925rem" },
                            }}
                          >
                            {items
                              .map((item) => `${item?.title || "Item"} (Qty: ${item?.quantity || 1})`)
                              .join(", ") || "No items"}
                          </Typography>

                          {/* Extra info tags */}
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
                            <Typography
                              variant="caption"
                              sx={{ color: "#64748b", fontWeight: 500, fontSize: "0.75rem" }}
                            >
                              {items.length} {items.length === 1 ? "item" : "items"}
                            </Typography>
                            {order.couponApplied && (
                              <Chip
                                label={`Coupon: ${order.couponApplied.couponCode || "APPLIED"}`}
                                size="small"
                                sx={{
                                  height: 19,
                                  fontSize: "0.625rem",
                                  fontWeight: 700,
                                  bgcolor: "#dcfce7",
                                  color: "#15803d",
                                }}
                              />
                            )}
                            {order.courierDetails?.partner && (
                              <Chip
                                label={`Via ${order.courierDetails.partner}`}
                                size="small"
                                sx={{
                                  height: 19,
                                  fontSize: "0.625rem",
                                  fontWeight: 600,
                                  bgcolor: "#f0f9ff",
                                  color: "#0369a1",
                                }}
                              />
                            )}
                          </Stack>
                        </Stack>
                      </Box>

                      {/* Desktop Actions on the Right */}
                      <Box
                        sx={{
                          display: { xs: "none", sm: "flex" },
                          flexDirection: "column",
                          alignItems: "flex-end",
                          justifyContent: "center",
                          gap: 1,
                          flexShrink: 0,
                          ml: 1,
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          endIcon={<KeyboardArrowRightIcon />}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 2,
                            borderColor: "#cbd5e1",
                            color: "#1e293b",
                            fontSize: "0.8125rem",
                            py: 0.6,
                            px: 1.5,
                            whiteSpace: "nowrap",
                            "&:hover": {
                              borderColor: BRAND_COLOR,
                              bgcolor: "rgba(7, 94, 84, 0.04)",
                              color: BRAND_COLOR,
                            },
                          }}
                        >
                          View Order Details
                        </Button>

                        {statusInfo.isPaymentPending && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate("/orderDetail", { state: { order: order, initiatePayment: true } });
                            }}
                            sx={{
                              bgcolor: BRAND_COLOR,
                              "&:hover": { bgcolor: "#004d40" },
                              textTransform: "none",
                              fontWeight: 700,
                              fontSize: "0.8125rem",
                              borderRadius: 2,
                              py: 0.6,
                              px: 1.5,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Pay Now
                          </Button>
                        )}
                      </Box>
                    </Box>

                    {/* Mobile Only Action Footer */}
                    <Box
                      sx={{
                        display: { xs: "flex", sm: "none" },
                        alignItems: "center",
                        gap: 1,
                        mt: 1.5,
                        pt: 1.25,
                        borderTop: "1px solid #f1f5f9",
                      }}
                    >
                      <Button
                        variant="outlined"
                        fullWidth
                        size="small"
                        endIcon={<KeyboardArrowRightIcon />}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 2,
                          borderColor: "#cbd5e1",
                          color: "#1e293b",
                          fontSize: "0.8125rem",
                          py: 0.7,
                          "&:hover": {
                            borderColor: BRAND_COLOR,
                            bgcolor: "rgba(7, 94, 84, 0.04)",
                            color: BRAND_COLOR,
                          },
                        }}
                      >
                        View Order Details
                      </Button>

                      {statusInfo.isPaymentPending && (
                        <Button
                          variant="contained"
                          fullWidth
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/orderDetail", { state: { order: order, initiatePayment: true } });
                          }}
                          sx={{
                            bgcolor: BRAND_COLOR,
                            "&:hover": { bgcolor: "#004d40" },
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.8125rem",
                            borderRadius: 2,
                            py: 0.7,
                          }}
                        >
                          Pay Now
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}

          {/* Enhanced Empty States */}
          {!loading &&
            filteredOrders.length === 0 &&
            detailedOrders.length > 0 && (
              <Card sx={{ mt: 4, p: { xs: 2, sm: 4 }, textAlign: "center" }}>
                <CardContent>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h1"
                      sx={{ fontSize: { xs: "3rem", sm: "4rem" }, mb: 2 }}
                    >
                      🔍
                    </Typography>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
                    >
                      No orders found
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                    >
                      We couldn't find any orders matching your search criteria.
                      Try adjusting your filters or search terms.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSearchText("");
                      }}
                      sx={{
                        mr: { xs: 1, sm: 2 },
                        minHeight: { xs: "40px", sm: "auto" },
                      }}
                    >
                      Clear Search
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => navigate("/")}
                      sx={{ minHeight: { xs: "40px", sm: "auto" } }}
                    >
                      Continue Shopping
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

          {!loading && detailedOrders.length === 0 && (
            <Card sx={{ mt: 4, p: { xs: 2, sm: 4 }, textAlign: "center" }}>
              <CardContent>
                <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                  <Typography
                    variant="h1"
                    sx={{ fontSize: { xs: "3rem", sm: "5rem" }, mb: 2 }}
                  >
                    📦
                  </Typography>
                  <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "1.5rem", sm: "2.125rem" },
                    }}
                  >
                    No orders yet
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      mb: { xs: 3, sm: 4 },
                      maxWidth: 400,
                      mx: "auto",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    You haven't placed any orders yet. Start shopping to see
                    your order history here.
                  </Typography>

                  {/* Action Buttons */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    justifyContent="center"
                    sx={{ mb: 4 }}
                  >
                    {user.cartItems !== undefined &&
                      user.cartItems !== null &&
                      user.cartItems.length > 0 && (
                        <Button
                          variant="contained"
                          size="large"
                          onClick={() => navigate("/cart")}
                          sx={{ minWidth: 160 }}
                        >
                          Complete Cart ({user.cartItems.length} items)
                        </Button>
                      )}
                    <Button
                      variant={
                        user.cartItems?.length > 0 ? "outlined" : "contained"
                      }
                      size="large"
                      onClick={() => navigate("/")}
                      sx={{ minWidth: 160 }}
                    >
                      Start Shopping
                    </Button>
                  </Stack>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* Product Suggestions */}
                <Box>
                  <ProductsContainer
                    heading="Discover Our Products"
                    showInRow={true}
                    addToCartEnable={true}
                    showCarousel={false}
                    productsArray={allProductsContext.state}
                  />
                </Box>

                {/* Recently Viewed Items */}
                {recentlyViewedItemsArray.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Divider sx={{ my: 4 }} />
                    <ProductsContainer
                      heading="Continue Where You Left Off"
                      showInRow={true}
                      addToCartEnable={true}
                      showCarousel={false}
                      productsArray={recentlyViewedItemsArray}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Container>
      )}
      {/* Enhanced Loading States */}
      {loading && (
        <Container 
          maxWidth="lg" 
          sx={{
            marginTop: { xs: "1rem", sm: "2rem" },
            marginBottom: { xs: "1rem", sm: "2rem" },
            px: { xs: 1, sm: 2 },
          }}
        >
          {/* Header Skeleton */}
          <Box sx={{ mb: 4 }}>
            {/* Title Skeleton */}
            <Skeleton 
              variant="text" 
              width="40%" 
              height={48} 
              sx={{ 
                mb: 1,
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" }
              }} 
            />
            {/* Description Skeleton */}
            <Skeleton 
              variant="text" 
              width="60%" 
              height={24} 
              sx={{ mb: 3 }}
            />
            {/* Search Bar Skeleton */}
            <Skeleton 
              variant="rounded" 
              height={56} 
              sx={{ borderRadius: 2 }}
            />
          </Box>

          {/* Order Cards Skeleton */}
          {[1, 2, 3].map((item) => (
            <Card key={item} sx={{ mb: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3} md={2}>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Skeleton variant="rounded" width={80} height={80} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={7}>
                    <Stack spacing={1}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Skeleton variant="rounded" width={100} height={24} />
                        <Skeleton variant="text" width={120} height={20} />
                      </Box>
                      <Skeleton variant="text" width="80%" height={24} />
                      <Skeleton variant="text" width="60%" height={20} />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3}>
                    <Stack alignItems="flex-end" spacing={1}>
                      <Skeleton variant="text" width={80} height={28} />
                      <Skeleton variant="text" width={100} height={16} />
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Container>
      )}
    </>
  );
};

export default UserOrdersPage;
